import { useEffect, useState, useCallback, useRef } from "react";
import { Navbar } from "@/components/Navbar";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Robot, UserCircle, CheckCircle, PaperPlaneRight, Headset, Plus, Trash, UploadSimple, FileText, Circle } from "@phosphor-icons/react";

const STATUS_STYLES = {
  needs_human: "text-amber-400 border-amber-400/40",
  ai_resolved: "text-green-400 border-green-400/40",
  in_progress: "text-cyan-400 border-cyan-400/40",
  resolved: "text-zinc-400 border-zinc-700",
};
const TICKET_FILTERS = [
  { key: "", label: "All" },
  { key: "needs_human", label: "Needs Human" },
  { key: "in_progress", label: "In Progress" },
  { key: "ai_resolved", label: "AI Resolved" },
  { key: "resolved", label: "Resolved" },
];
const PRODUCT_SUGGESTIONS = ["AI iD Cards", "Hybrid Customer Support", "SIMON", "Caliber ATS", "Compliance First Solutions", "Match My Resume — Shift Happens"];
const TABS = [
  { key: "tickets", label: "Tickets" },
  { key: "chats", label: "Live Chats" },
  { key: "agents", label: "Agents" },
  { key: "build", label: "Assisted Build" },
];

export default function SupportDashboard() {
  const [tab, setTab] = useState("chats");
  const [status, setStatus] = useState("away");

  useEffect(() => { api.get("/support/status").then((r) => setStatus(r.data.status)).catch(() => {}); }, []);
  const toggleStatus = async () => {
    const next = status === "available" ? "away" : "available";
    try { const { data } = await api.put("/support/status", { status: next }); setStatus(data.status); toast.success(`You're now ${data.status === "available" ? "Available — new chats route to you first" : "Away — AI agents take chats"}`); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 pt-28 pb-20">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-zinc-800 pb-6">
          <div>
            <p className="font-mono-p text-xs tracking-[0.3em] uppercase text-cyan-400 mb-2">// Support Desk</p>
            <h1 className="font-mono-p font-bold uppercase tracking-tight text-3xl">Command Center</h1>
          </div>
          <button data-testid="status-toggle" onClick={toggleStatus}
            className={`flex items-center gap-2 px-4 py-2.5 border font-mono-p text-xs uppercase tracking-widest transition-colors ${status === "available" ? "border-green-400 text-green-400 bg-green-400/5" : "border-zinc-700 text-zinc-400 hover:text-zinc-200"}`}>
            <Circle size={12} weight="fill" className={status === "available" ? "text-green-400" : "text-zinc-600"} />
            {status === "available" ? "Available (you take chats first)" : "Away (AI agents on duty)"}
          </button>
        </div>

        <div className="mt-6 flex gap-2">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`font-mono-p text-xs uppercase tracking-widest px-5 py-2.5 border transition-colors ${tab === t.key ? "border-cyan-400 text-cyan-400" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "tickets" && <TicketsTab />}
          {tab === "chats" && <LiveChatsTab />}
          {tab === "agents" && <AgentsTab />}
          {tab === "build" && <AssistedBuildTab />}
        </div>
      </main>
    </div>
  );
}

/* ---------------- Tickets ---------------- */
function AssistedBuildTab() {
  const [form, setForm] = useState({ email: "", role_title: "", activation_key: "", member_number: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const send = async () => {
    if (!form.email.trim()) { toast.error("Customer email is required"); return; }
    setBusy(true); setSent(null);
    try {
      const { data } = await api.post("/support/send-card-pdf", {
        email: form.email.trim(), role_title: form.role_title.trim() || null,
        activation_key: form.activation_key.trim() || null, member_number: form.member_number.trim() || null,
      });
      setSent(data.download_url);
      toast.success(`AI iD Card emailed to ${form.email.trim()}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const FIELDS = [
    { k: "email", label: "Customer Email", ph: "customer@example.com" },
    { k: "role_title", label: "Role / Title", ph: "e.g. Operations Specialist" },
    { k: "activation_key", label: "Activation Key", ph: "SIM-XXXXXX-ON" },
    { k: "member_number", label: "Member Number", ph: "MN-XXXXXX-AI" },
  ];
  return (
    <div className="max-w-2xl" data-testid="assisted-build-tab">
      <h2 className="font-mono-p font-bold tracking-tight text-xl">Assisted AI iD Card Build</h2>
      <p className="mt-1 text-sm text-zinc-500">Fill this in with the customer while you talk, then email them their AI iD Card as a secure, private PDF download link (expires in 30 days).</p>
      <div className="mt-6 grid gap-4">
        {FIELDS.map((f) => (
          <div key={f.k}>
            <label className="font-mono-p text-[11px] uppercase tracking-widest text-zinc-500">{f.label}</label>
            <input data-testid={`build-${f.k}`} value={form[f.k]} onChange={set(f.k)} placeholder={f.ph}
              className="mt-1.5 w-full bg-black border border-zinc-800 px-3 py-2.5 text-sm text-zinc-100 focus:border-cyan-400/60 focus:outline-none" />
          </div>
        ))}
      </div>
      <button data-testid="send-card-pdf-btn" onClick={send} disabled={busy}
        className="mt-6 inline-flex items-center gap-2 font-mono-p text-sm uppercase tracking-widest bg-cyan-400 text-black px-5 py-3 font-bold hover:bg-cyan-300 transition-colors disabled:opacity-50">
        <PaperPlaneRight size={16} weight="bold" /> {busy ? "Sending…" : "Email AI iD Card PDF"}
      </button>
      {sent && (
        <div data-testid="send-card-result" className="mt-4 border border-green-400/30 bg-green-400/5 p-3 text-xs text-green-300 break-all">
          Emailed. Secure download link: {sent}
        </div>
      )}
    </div>
  );
}

function TicketsTab() {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");

  const load = useCallback(() => api.get(`/support/tickets${filter ? `?status=${filter}` : ""}`).then((r) => setTickets(r.data)).catch((e) => toast.error(apiError(e))), [filter]);
  useEffect(() => { load(); }, [load]);
  const open = (t) => api.get(`/support/tickets/${t.id}`).then((r) => setActive(r.data)).catch(() => {});
  const sendReply = async (resolve) => {
    if (!reply.trim()) return;
    try { const { data } = await api.post(`/support/tickets/${active.id}/reply`, { message: reply, resolve }); setActive(data); setReply(""); load(); toast.success(resolve ? "Replied & resolved" : "Reply sent"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {TICKET_FILTERS.map((f) => (
          <button key={f.key} data-testid={`filter-${f.key || "all"}`} onClick={() => { setFilter(f.key); setActive(null); }}
            className={`font-mono-p text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${filter === f.key ? "border-cyan-400 text-cyan-400" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}>{f.label}</button>
        ))}
      </div>
      <div className="mt-6 grid lg:grid-cols-[380px_1fr] gap-4">
        <div className="border border-zinc-800 bg-[#090A0C] max-h-[68vh] overflow-y-auto">
          {tickets.length === 0 && <p className="p-6 text-sm text-zinc-500">No tickets.</p>}
          {tickets.map((t) => (
            <button key={t.id} data-testid={`ticket-${t.id}`} onClick={() => open(t)} className={`w-full text-left px-5 py-4 border-b border-zinc-900 hover:bg-[#0c0e12] transition-colors ${active?.id === t.id ? "bg-[#0c0e12]" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-sm text-zinc-100 truncate">{t.subject}</span>
                <span className={`font-mono-p text-[9px] uppercase tracking-wider px-2 py-0.5 border shrink-0 ${STATUS_STYLES[t.status] || "text-zinc-500 border-zinc-700"}`}>{t.status.replace("_", " ")}</span>
              </div>
              <p className="text-xs text-zinc-500 mt-1">{t.name} · {t.email}</p>
              <p className="text-[10px] text-zinc-600 mt-1 uppercase font-mono-p">{t.product ? `${t.product} · ` : ""}{t.category}</p>
            </button>
          ))}
        </div>
        <div className="border border-zinc-800 bg-[#090A0C] p-6 max-h-[68vh] overflow-y-auto">
          {!active ? <p className="text-sm text-zinc-500">Select a ticket to view the conversation.</p> : (
            <>
              <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
                <div><h2 className="font-bold text-lg">{active.subject}</h2><p className="text-xs text-zinc-500">{active.name} · {active.email} · {active.category}</p></div>
                <span className={`font-mono-p text-[10px] uppercase tracking-wider px-2 py-1 border ${STATUS_STYLES[active.status] || ""}`}>{active.status.replace("_", " ")}</span>
              </div>
              <div className="mt-5 space-y-4">
                {active.thread.map((m, i) => (
                  <div key={i} data-testid={`thread-${i}`} className="flex gap-2">
                    {m.role === "ai" ? <Robot size={20} className="text-cyan-400 shrink-0 mt-1" /> : m.role === "human" ? <CheckCircle size={20} className="text-green-400 shrink-0 mt-1" /> : <UserCircle size={20} className="text-zinc-500 shrink-0 mt-1" />}
                    <div className="flex-1"><p className="text-[10px] uppercase font-mono-p text-zinc-500">{m.author} · {m.role}</p><p className="text-sm text-zinc-200 whitespace-pre-wrap mt-1">{m.text}</p></div>
                  </div>
                ))}
              </div>
              <div className="mt-6 border-t border-zinc-800 pt-4">
                <textarea data-testid="reply-input" rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply..." className="w-full bg-black border border-zinc-800 px-3 py-2.5 text-sm focus:border-cyan-400/60 focus:outline-none resize-y" />
                <div className="flex gap-2 mt-3">
                  <button data-testid="reply-send" onClick={() => sendReply(false)} className="flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest bg-cyan-400 text-black px-4 py-2.5 font-bold hover:bg-cyan-300 transition-colors"><PaperPlaneRight size={14} weight="bold" /> Reply</button>
                  <button data-testid="reply-resolve" onClick={() => sendReply(true)} className="font-mono-p text-xs uppercase tracking-widest border border-green-400/40 text-green-400 px-4 py-2.5 hover:bg-green-400/5 transition-colors">Reply & Resolve</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ---------------- Live Chats ---------------- */
function LiveChatsTab() {
  const [chats, setChats] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [nowTs, setNowTs] = useState(Date.now());
  const activeIdRef = useRef(null);

  const loadList = useCallback(() => api.get("/support/live-chats").then((r) => setChats(r.data)).catch((e) => toast.error(apiError(e))), []);
  const loadDetail = useCallback((id) => api.get(`/support/live-chats/${id}`).then((r) => setActive(r.data)).catch(() => {}), []);
  useEffect(() => { loadList(); const id = setInterval(loadList, 3000); return () => clearInterval(id); }, [loadList]);
  useEffect(() => { const id = setInterval(() => setNowTs(Date.now()), 1000); return () => clearInterval(id); }, []);

  const secsLeft = (c) => {
    if (c.status !== "pending_claim" || !c.claim_deadline) return 0;
    return Math.max(0, Math.ceil((new Date(c.claim_deadline).getTime() - nowTs) / 1000));
  };
  const claimChat = async (e, id) => {
    e.stopPropagation();
    try { const { data } = await api.post(`/support/live-chats/${id}/takeover`); setActive(data); loadList(); toast.success("You've taken this chat — now live"); }
    catch (err) { toast.error(apiError(err)); }
  };
  useEffect(() => {
    activeIdRef.current = active?.id || null;
    if (!active?.id) return;
    const id = setInterval(() => loadDetail(activeIdRef.current), 4000);
    return () => clearInterval(id);
  }, [active?.id, loadDetail]);

  const takeover = async () => {
    try { const { data } = await api.post(`/support/live-chats/${active.id}/takeover`); setActive(data); loadList(); toast.success("You've taken over — chatting live"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    try { const { data } = await api.post(`/support/live-chats/${active.id}/reply`, { message: reply }); setActive(data); setReply(""); loadList(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const close = async () => {
    try { await api.post(`/support/live-chats/${active.id}/close`); setActive(null); loadList(); toast.success("Chat closed"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-4">
      <div className="border border-zinc-800 bg-[#090A0C] max-h-[70vh] overflow-y-auto">
        {chats.length === 0 && <p className="p-6 text-sm text-zinc-500">No live chats yet.</p>}
        {chats.map((c) => (
          <button key={c.id} data-testid={`livechat-${c.id}`} onClick={() => loadDetail(c.id)} className={`w-full text-left px-5 py-4 border-b border-zinc-900 hover:bg-[#0c0e12] transition-colors ${c.status === "pending_claim" ? "bg-amber-500/5 border-l-2 border-l-amber-400" : active?.id === c.id ? "bg-[#0c0e12]" : ""}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-sm text-zinc-100 truncate">{c.visitor_name || "Visitor"}</span>
              <span className={`font-mono-p text-[9px] uppercase tracking-wider px-2 py-0.5 border shrink-0 ${c.status === "closed" ? "text-zinc-500 border-zinc-700" : c.status === "pending_claim" ? "text-amber-400 border-amber-400/50" : c.assignee === "human" ? "text-green-400 border-green-400/40" : "text-cyan-400 border-cyan-400/40"}`}>{c.status === "closed" ? "closed" : c.status === "pending_claim" ? "new" : c.assignee}</span>
            </div>
            <p className="text-[10px] text-zinc-600 mt-1 uppercase font-mono-p">{c.product} · {c.agent_name}</p>
            <p className="text-xs text-zinc-500 mt-1 truncate">{c.last}</p>
            {c.status === "pending_claim" && (
              <div className="flex items-center justify-between gap-2 mt-2" data-testid={`claim-row-${c.id}`}>
                <span className="text-[10px] font-mono-p uppercase text-amber-400">
                  {secsLeft(c) > 0 ? `Take within ${secsLeft(c)}s — or AI takes over` : "Routing to AI…"}
                </span>
                <span data-testid={`claim-btn-${c.id}`} role="button" tabIndex={0} onClick={(e) => claimChat(e, c.id)}
                  className="font-mono-p text-[10px] uppercase tracking-widest bg-amber-400 text-black px-3 py-1.5 font-bold hover:bg-amber-300 transition-colors cursor-pointer">
                  Take Chat
                </span>
              </div>
            )}
          </button>
        ))}
      </div>
      <div className="border border-zinc-800 bg-[#090A0C] flex flex-col max-h-[70vh]">
        {!active ? <p className="p-6 text-sm text-zinc-500">Select a chat.</p> : (
          <>
            <div className="flex items-center justify-between border-b border-zinc-800 p-5">
              <div>
                <h2 className="font-bold text-lg">{active.visitor_name || "Visitor"}</h2>
                <p className="text-xs text-zinc-500">{active.product} · assigned to {active.agent_name} ({active.assignee})</p>
              </div>
              <div className="flex gap-2">
                {active.assignee !== "human" && active.status !== "closed" && (
                  <button data-testid="takeover-btn" onClick={takeover} className="flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest border border-green-400/50 text-green-400 px-3 py-2 hover:bg-green-400/5 transition-colors"><Headset size={14} /> Take over</button>
                )}
                {active.status !== "closed" && <button data-testid="close-chat-btn" onClick={close} className="font-mono-p text-xs uppercase tracking-widest border border-zinc-700 text-zinc-400 px-3 py-2 hover:text-zinc-200 transition-colors">Close</button>}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {(active.thread || []).map((m, i) => (
                m.role === "system" ? (
                  <div key={i} className="text-center"><span className="inline-block text-[10px] uppercase tracking-widest font-mono-p text-zinc-500 bg-black border border-zinc-800 px-3 py-1">{m.text}</span></div>
                ) : (
                  <div key={i} className={`flex gap-2 ${m.role === "customer" ? "" : "flex-row-reverse"}`}>
                    {m.role === "customer" ? <UserCircle size={20} className="text-zinc-500 shrink-0 mt-1" /> : m.role === "human" ? <Headset size={20} className="text-green-400 shrink-0 mt-1" /> : <Robot size={20} className="text-cyan-400 shrink-0 mt-1" />}
                    <div className={`max-w-[80%] px-4 py-2.5 text-sm ${m.role === "customer" ? "bg-black border border-zinc-800 text-zinc-300" : m.role === "human" ? "bg-green-500/10 border border-green-500/40 text-zinc-100" : "bg-black border border-cyan-500/30 text-zinc-300"}`}>
                      <p className="text-[10px] uppercase font-mono-p text-zinc-500 mb-1">{m.author}</p>
                      <p className="whitespace-pre-wrap">{m.text}</p>
                    </div>
                  </div>
                )
              ))}
            </div>
            {active.status !== "closed" && (
              <div className="p-4 border-t border-zinc-800 flex items-center gap-2">
                <input data-testid="livechat-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder={active.assignee === "human" ? "Reply to the customer..." : "Take over to reply, or type to jump in..."} className="flex-1 bg-black border border-zinc-800 px-4 py-2.5 text-sm focus:border-cyan-400/60 focus:outline-none" />
                <button data-testid="livechat-reply-send" onClick={sendReply} className="w-10 h-10 bg-green-500 text-black flex items-center justify-center hover:bg-green-400 transition-colors"><PaperPlaneRight size={18} weight="fill" /></button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- Agents ---------------- */
function AgentsTab() {
  const [agents, setAgents] = useState([]);
  const [collabs, setCollabs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ collab_id: "", product: "", priority: 1, chat_cap: 10, intro: "" });

  const load = useCallback(() => api.get("/support/agents").then((r) => setAgents(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); api.get("/support/my-collaborators").then((r) => setCollabs(r.data)).catch(() => {}); }, [load]);

  const create = async () => {
    if (!form.collab_id || !form.product.trim()) { toast.error("Pick an agent and a product"); return; }
    try { await api.post("/support/agents", { ...form, priority: Number(form.priority) || 1, chat_cap: Number(form.chat_cap) || 10 }); setShowAdd(false); setForm({ collab_id: "", product: "", priority: 1, chat_cap: 10, intro: "" }); load(); toast.success("Agent added to the desk"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-zinc-500">Assign your S.I.M.O.N. agents to product lines. Set priority (1 = first to answer) and a concurrent-chat cap for overflow.</p>
        <button data-testid="add-agent-btn" onClick={() => setShowAdd((v) => !v)} className="flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest bg-cyan-400 text-black px-4 py-2.5 font-bold hover:bg-cyan-300 transition-colors"><Plus size={14} weight="bold" /> Add Agent</button>
      </div>

      {showAdd && (
        <div data-testid="add-agent-form" className="border border-cyan-400/30 bg-[#090A0C] p-5 mb-5 grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase font-mono-p text-zinc-500">Agent (your S.I.M.O.N. collaborator)</label>
            <select data-testid="agent-collab" value={form.collab_id} onChange={(e) => setForm({ ...form, collab_id: e.target.value })} className="w-full mt-1 bg-black border border-zinc-800 px-3 py-2.5 text-sm focus:border-cyan-400/60 focus:outline-none">
              <option value="">Select an agent…</option>
              {collabs.map((c) => <option key={c.id} value={c.id}>{c.name}{c.role ? ` — ${c.role}` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase font-mono-p text-zinc-500">Product line they champion</label>
            <input data-testid="agent-product" list="product-suggestions" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="e.g. SIMON" className="w-full mt-1 bg-black border border-zinc-800 px-3 py-2.5 text-sm focus:border-cyan-400/60 focus:outline-none" />
            <datalist id="product-suggestions">{PRODUCT_SUGGESTIONS.map((p) => <option key={p} value={p} />)}</datalist>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-[10px] uppercase font-mono-p text-zinc-500">Priority</label><input data-testid="agent-priority" type="number" min="1" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full mt-1 bg-black border border-zinc-800 px-3 py-2.5 text-sm focus:border-cyan-400/60 focus:outline-none" /></div>
            <div><label className="text-[10px] uppercase font-mono-p text-zinc-500">Chat cap</label><input data-testid="agent-cap" type="number" min="1" value={form.chat_cap} onChange={(e) => setForm({ ...form, chat_cap: e.target.value })} className="w-full mt-1 bg-black border border-zinc-800 px-3 py-2.5 text-sm focus:border-cyan-400/60 focus:outline-none" /></div>
          </div>
          <div><label className="text-[10px] uppercase font-mono-p text-zinc-500">Greeting (optional)</label><input value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} placeholder="Hi, I'm your specialist…" className="w-full mt-1 bg-black border border-zinc-800 px-3 py-2.5 text-sm focus:border-cyan-400/60 focus:outline-none" /></div>
          <div className="md:col-span-2 flex gap-2">
            <button data-testid="agent-save" onClick={create} className="font-mono-p text-xs uppercase tracking-widest bg-cyan-400 text-black px-5 py-2.5 font-bold hover:bg-cyan-300 transition-colors">Save Agent</button>
            <button onClick={() => setShowAdd(false)} className="font-mono-p text-xs uppercase tracking-widest border border-zinc-700 text-zinc-400 px-5 py-2.5 hover:text-zinc-200 transition-colors">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {agents.length === 0 && !showAdd && <p className="text-sm text-zinc-500 border border-zinc-800 bg-[#090A0C] p-6">No agents on the desk yet. Add one to start routing chats.</p>}
        {agents.map((a) => <AgentRow key={a.id} agent={a} onChanged={load} />)}
      </div>
    </div>
  );
}

function AgentRow({ agent, onChanged }) {
  const [a, setA] = useState(agent);
  const fileRef = useRef(null);
  useEffect(() => { setA(agent); }, [agent]);

  const saveField = async (patch) => {
    const next = { ...a, ...patch };
    setA(next);
    try { const { data } = await api.put(`/support/agents/${a.id}`, patch); setA(data); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post(`/support/agents/${a.id}/knowledge`, fd); setA(data); toast.success(`Added "${f.name}" to ${a.name}'s knowledge`); }
    catch (err) { toast.error(apiError(err)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  const delKnowledge = async (kid) => {
    try { const { data } = await api.delete(`/support/agents/${a.id}/knowledge/${kid}`); setA(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${a.name} from the support desk?`)) return;
    try { await api.delete(`/support/agents/${a.id}`); onChanged(); toast.success("Agent removed"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid={`agent-row-${a.id}`} className={`border bg-[#090A0C] p-5 ${a.active ? "border-zinc-800" : "border-zinc-900 opacity-60"}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Robot size={26} weight="duotone" className="text-cyan-400" />
          <div>
            <p className="font-bold text-zinc-100">{a.name}</p>
            <p className="text-xs text-zinc-500 font-mono-p uppercase tracking-wide">{a.product}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <label className="text-[10px] uppercase font-mono-p text-zinc-500 flex items-center gap-1">Priority
            <input data-testid={`agent-priority-${a.id}`} type="number" min="1" value={a.priority} onChange={(e) => setA({ ...a, priority: e.target.value })} onBlur={() => saveField({ priority: Number(a.priority) || 1 })} className="w-14 bg-black border border-zinc-800 px-2 py-1 text-sm text-zinc-200 text-center ml-1" />
          </label>
          <label className="text-[10px] uppercase font-mono-p text-zinc-500 flex items-center gap-1">Cap
            <input data-testid={`agent-cap-${a.id}`} type="number" min="1" value={a.chat_cap} onChange={(e) => setA({ ...a, chat_cap: e.target.value })} onBlur={() => saveField({ chat_cap: Number(a.chat_cap) || 10 })} className="w-14 bg-black border border-zinc-800 px-2 py-1 text-sm text-zinc-200 text-center ml-1" />
          </label>
          <button data-testid={`agent-active-${a.id}`} onClick={() => saveField({ active: !a.active })} className={`font-mono-p text-[10px] uppercase tracking-widest px-3 py-1.5 border ${a.active ? "border-green-400/50 text-green-400" : "border-zinc-700 text-zinc-500"}`}>{a.active ? "Active" : "Inactive"}</button>
          <button data-testid={`agent-delete-${a.id}`} onClick={remove} className="text-zinc-500 hover:text-red-400 transition-colors"><Trash size={18} /></button>
        </div>
      </div>

      <div className="mt-4 border-t border-zinc-900 pt-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase font-mono-p text-zinc-500">Knowledge Base — FAQ · SOPs · step-by-step guides</p>
          <button data-testid={`agent-upload-${a.id}`} onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 font-mono-p text-[10px] uppercase tracking-widest text-cyan-400 border border-cyan-400/30 px-3 py-1.5 hover:bg-cyan-400/5 transition-colors"><UploadSimple size={13} /> Upload doc</button>
          <input ref={fileRef} type="file" accept=".txt,.md,.pdf,.docx,.csv,.json" onChange={upload} className="hidden" />
        </div>
        {(!a.knowledge || a.knowledge.length === 0) ? (
          <p className="text-xs text-zinc-600">No documents yet. Upload FAQ / SOP / escalation guides to ground {a.name}'s answers.</p>
        ) : (
          <div className="space-y-1">
            {a.knowledge.map((k) => (
              <div key={k.id} className="flex items-center justify-between bg-black border border-zinc-800 px-3 py-2 text-xs">
                <span className="flex items-center gap-2 text-zinc-300 truncate"><FileText size={14} className="text-zinc-500" /> {k.name} <span className="text-zinc-600">· {k.chars.toLocaleString()} chars</span></span>
                <button data-testid={`knowledge-del-${k.id}`} onClick={() => delKnowledge(k.id)} className="text-zinc-500 hover:text-red-400 transition-colors shrink-0"><Trash size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
